/**
 * First-session setup wizard (Zone 2) — `/app/:tenantSlug/welcome`.
 *
 * Four steps, every one skippable, nothing here is the only way to do its job:
 *   1. Team — confirm/rename the portal (PATCH /tenants/v1/{id}).
 *   2. Staff — invite people with a persona (POST /users/v1, invite path);
 *      invite links are shown for copying.
 *   3. Playbook — seed the first team options (POST /playbook/v1/stats/…).
 *   4. Done — where to go next (events, play entry, docs).
 *
 * Signup seeds catalogs server-side; this wizard covers the human half a new
 * Team Admin used to have to discover page by page.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import type { CreatedTeamUser, StatCategory } from '../../lib/api-types';
import { APP_VIEWS, PRODUCT_NAME } from '../../lib/constants';
import { PERSONA_LABELS } from '../../lib/personas';
import { STAT_DEFINITIONS, statDefinition } from '../playbook/stat-catalog';

const STEPS = ['Team', 'Staff', 'Playbook', 'Done'] as const;

/** Personas a Team Admin typically invites first (never another admin here). */
const INVITE_PERSONAS = [
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'primary_stat_operator',
  'general_stat_operator',
] as const;

function toMessage(error: unknown, fallback: string): string {
  return ApiError.isApiError(error) ? error.message : fallback;
}

export function SetupWizardPage(): JSX.Element {
  const { persona, tenantSlug } = useSession();
  const [step, setStep] = useState(0);
  const base = `/app/${tenantSlug ?? ''}`;

  if (persona !== 'team_admin') {
    return (
      <div className="wizard" data-testid="setup-wizard">
        <h1>Team setup</h1>
        <p className="muted">
          Only a Team Admin runs the setup wizard. You’re all set — head to{' '}
          <Link to={base}>your team home</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="wizard" data-testid="setup-wizard">
      <header className="wizard__head">
        <h1>Set up your team</h1>
        <ol className="wizard__steps" aria-label="Setup progress">
          {STEPS.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className="wizard__step"
              data-state={index === step ? 'current' : index < step ? 'done' : 'todo'}
            >
              <span className="wizard__step-bar" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ol>
      </header>

      {step === 0 ? <TeamStep onNext={() => setStep(1)} /> : null}
      {step === 1 ? <StaffStep onBack={() => setStep(0)} onNext={() => setStep(2)} /> : null}
      {step === 2 ? <PlaybookStep onBack={() => setStep(1)} onNext={() => setStep(3)} /> : null}
      {step === 3 ? <DoneStep base={base} /> : null}
    </div>
  );
}

/* --------------------------------------------------------------------- step 1 */

function TeamStep({ onNext }: { onNext: () => void }): JSX.Element {
  const apiClient = useApiClient();
  const { claims, session, tenantSlug } = useSession();
  const { showToast } = useToast();
  const tenantId = claims?.tid ?? session?.tenantId ?? null;

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const save = async (): Promise<void> => {
    if (tenantId === null || name.trim() === '') {
      onNext();
      return;
    }
    setIsBusy(true);
    try {
      await apiClient.updateTenant(tenantId, { name: name.trim() });
      showToast('Team name updated.', 'success');
      onNext();
    } catch (caught) {
      setError(toMessage(caught, 'The team name could not be saved.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section className="wizard__panel" aria-label="Team">
      <h2>Your team portal</h2>
      <p className="muted">
        Signup already created the portal at <span className="mono">/app/{tenantSlug}</span> and
        seeded the stat catalog. Give the portal its display name — or keep what you entered at
        signup.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      <TextField
        label="Team name (optional)"
        name="team_name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="e.g. Westview Eagles Varsity"
      />
      <div className="wizard__actions">
        <button
          className="btn btn--primary"
          disabled={isBusy}
          onClick={() => void save()}
          type="button"
        >
          {name.trim() === '' ? 'Looks right — continue' : isBusy ? 'Saving…' : 'Save and continue'}
        </button>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------- step 2 */

function StaffStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }): JSX.Element {
  const apiClient = useApiClient();
  const [email, setEmail] = useState('');
  const [personaChoice, setPersonaChoice] = useState<string>('coach');
  const [invited, setInvited] = useState<CreatedTeamUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const invite = async (): Promise<void> => {
    if (!email.includes('@')) {
      setError('Enter the email address to invite.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const created = await apiClient.createTeamUser({
        email: email.trim(),
        persona: personaChoice as never,
        send_invite: true,
      });
      setInvited((current) => [...current, created]);
      setEmail('');
    } catch (caught) {
      setError(toMessage(caught, 'The invite could not be created.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section className="wizard__panel" aria-label="Staff">
      <h2>Invite your staff</h2>
      <p className="muted">
        Each person gets one persona — it decides what they see when they sign in with your team
        code. You can always add more later under User Management.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      <form
        className="wizard__invite"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void invite();
        }}
      >
        <TextField
          label="Email"
          name="invite_email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label className="field">
          <span className="field__label">Persona</span>
          <select
            className="field__control"
            onChange={(event) => setPersonaChoice(event.target.value)}
            value={personaChoice}
          >
            {INVITE_PERSONAS.map((value) => (
              <option key={value} value={value}>
                {PERSONA_LABELS[value] ?? value}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn--secondary" disabled={isBusy} type="submit">
          {isBusy ? 'Inviting…' : 'Send invite'}
        </button>
      </form>

      {invited.length > 0 ? (
        <ul className="wizard__invited">
          {invited.map((user) => (
            <li key={user.user_id}>
              <strong>{user.email}</strong> — {user.persona}
              {user.invite_url !== null ? (
                <span className="muted">
                  {' '}
                  · invite link: <span className="mono">{user.invite_url}</span>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="wizard__actions">
        <button className="btn btn--ghost" onClick={onBack} type="button">
          Back
        </button>
        <button className="btn btn--primary" onClick={onNext} type="button">
          {invited.length > 0 ? 'Continue' : 'Skip for now'}
        </button>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------- step 3 */

function PlaybookStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }): JSX.Element {
  const apiClient = useApiClient();
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<StatCategory>('OFF_CONCEPT');
  const [added, setAdded] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const add = async (): Promise<void> => {
    if (label.trim() === '') {
      setError('Name the option.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const created = await apiClient.createStatOption(category, 'own', { label: label.trim() });
      setAdded((current) => [...current, `${statDefinition(category).label}: ${created.label}`]);
      setLabel('');
    } catch (caught) {
      setError(toMessage(caught, 'The option could not be added.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section className="wizard__panel" aria-label="Playbook">
      <h2>Seed your playbook</h2>
      <p className="muted">
        Every stat (concepts, formations, fronts, coverages, blitzes…) comes with stock options. Add
        a few of your own team&apos;s names now — the full editor, opponent lists and on/off
        switches live on the Playbook page.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      <form
        className="wizard__invite"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <label className="field">
          <span className="field__label">Stat</span>
          <select
            className="field__control"
            onChange={(event) => setCategory(event.target.value as StatCategory)}
            value={category}
          >
            {STAT_DEFINITIONS.filter((d) => d.sides.includes('own')).map((d) => (
              <option key={d.category} value={d.category}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <TextField
          label="Option name"
          name="option_label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="e.g. Hawk Sweep"
        />
        <button className="btn btn--secondary" disabled={isBusy} type="submit">
          {isBusy ? 'Adding…' : 'Add option'}
        </button>
      </form>
      {added.length > 0 ? (
        <ul className="wizard__list" aria-label="Options added">
          {added.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}

      <div className="wizard__actions">
        <button className="btn btn--ghost" onClick={onBack} type="button">
          Back
        </button>
        <button className="btn btn--primary" onClick={onNext} type="button">
          {added.length > 0 ? 'Continue' : 'Skip for now'}
        </button>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------- step 4 */

function DoneStep({ base }: { base: string }): JSX.Element {
  return (
    <section className="wizard__panel" aria-label="Done">
      <h2>You’re ready for kickoff</h2>
      <p className="muted">{PRODUCT_NAME} is set up. The usual next steps, in game order:</p>
      <ul className="wizard__invited">
        <li>
          <Link to={`${base}/${APP_VIEWS.events}`}>Schedule your first event</Link> — plays always
          belong to an event.
        </li>
        <li>
          <Link to={`${base}/${APP_VIEWS.roster}`}>Fill in the roster</Link> so operators can tag
          players.
        </li>
        <li>
          <Link to={`${base}/docs/getting-started`}>Read the getting-started guide</Link> — five
          minutes, covers the whole flow.
        </li>
      </ul>
      <div className="wizard__actions">
        <Link className="btn btn--primary" to={base}>
          Go to team home
        </Link>
      </div>
    </section>
  );
}
